import { useRef, useEffect, useState } from "react";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";
import GroupInfo from "../GroupInfo";
import {
  BackIcon,
  PhoneIcon,
  VideoIcon,
  MoreIcon,
  SearchIcon,
} from "../../../svg/chat";
import { PulseLoader } from "react-spinners";
import "./style.css";

export default function ChatWindow({
  conversation,
  messages,
  onlineUsers,
  typingUsers,
  onSendMessage,
  onTypingStart,
  onTypingStop,
  onBack,
  visible,
  user,
  loading,
  onStartCall,
  onReact,
  onDelete,
}) {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [replyTo, setReplyTo] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  // Handle scroll to detect if user scrolled up
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    setShowScrollButton(!isNearBottom);
    if (isNearBottom) {
      setUnreadCount(0);
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowScrollButton(false);
    setUnreadCount(0);
  };

  // Scroll to bottom on new messages (only if near bottom)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (messages.length > 0) {
      // Increment unread count if not near bottom
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.sender?._id !== user?.id) {
        setUnreadCount((prev) => prev + 1);
      }
    }
  }, [messages, user?.id]);

  if (!conversation) {
    return (
      <div className={`chat_window empty ${visible ? "visible" : "hidden"}`}>
        <div className="empty_chat">
          <img
            src="/assets/chat-illustration.svg"
            alt="Select a conversation"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <h3>Welcome to Socializa Chat</h3>
          <p>Select a conversation to start messaging</p>
        </div>
      </div>
    );
  }

  const getConversationInfo = () => {
    if (conversation.type === "group") {
      return {
        name: conversation.group?.name || "Group",
        picture: conversation.group?.picture,
        subtitle: `${conversation.participants?.length || 0} members`,
        isOnline: false,
      };
    }

    const otherUser = conversation.participants?.find((p) => p._id !== user.id);
    const isOnline = onlineUsers.includes(otherUser?._id);

    return {
      name: `${otherUser?.first_name || ""} ${otherUser?.last_name || ""}`,
      picture: otherUser?.picture,
      subtitle: isOnline ? "Online" : "Offline",
      isOnline,
      userId: otherUser?._id,
    };
  };

  const info = getConversationInfo();
  const isTyping = typingUsers[conversation._id];

  const handleReply = (message) => {
    setReplyTo(message);
  };

  const handleCancelReply = () => {
    setReplyTo(null);
  };

  const handleSendMessage = (messageData) => {
    onSendMessage({
      ...messageData,
      replyTo: replyTo?._id,
    });
    setReplyTo(null);
  };

  return (
    <div className={`chat_window ${visible ? "visible" : "hidden"}`}>
      {/* Header */}
      <div className="chat_window_header">
        <button className="back_btn mobile_only" onClick={onBack}>
          <BackIcon />
        </button>

        <div
          className={`chat_user_info ${conversation.type === "group" ? "clickable" : ""}`}
          onClick={() => conversation.type === "group" && setShowGroupInfo(true)}
        >
          <div className="chat_avatar">
            <img
              src={info.picture}
              alt={info.name}
              onError={(e) => {
                e.target.src =
                  "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
              }}
            />
            {info.isOnline && <span className="online_dot"></span>}
          </div>
          <div className="chat_user_details">
            <span className="chat_user_name">{info.name}</span>
            <span className="chat_user_status">
              {isTyping ? (
                <span className="typing_text">typing...</span>
              ) : (
                info.subtitle
              )}
            </span>
          </div>
        </div>

        <div className="chat_header_actions">
          {conversation.type !== "group" && (
            <>
              <button
                className="header_action_btn call_btn"
                title="Chamada de Voz"
                onClick={() => onStartCall && onStartCall("voice")}
              >
                <PhoneIcon color="#31a24c" />
              </button>
              <button
                className="header_action_btn video_call_btn"
                title="Chamada de Vídeo"
                onClick={() => onStartCall && onStartCall("video")}
              >
                <VideoIcon color="#1877f2" />
              </button>
            </>
          )}
          <button className="header_action_btn" title="Pesquisar">
            <SearchIcon />
          </button>
          <button
            className="header_action_btn"
            title="Mais opções"
            onClick={() => setShowOptions(!showOptions)}
          >
            <MoreIcon />
          </button>
        </div>

        {/* Options dropdown */}
        {showOptions && (
          <div className="chat_options_dropdown">
            <button onClick={() => setShowOptions(false)}>View Profile</button>
            <button onClick={() => setShowOptions(false)}>Search in Chat</button>
            <button onClick={() => setShowOptions(false)}>Mute</button>
            <button onClick={() => setShowOptions(false)}>Block</button>
            <button onClick={() => setShowOptions(false)} className="danger">
              Delete Chat
            </button>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div
        className="messages_container"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading ? (
          <div className="loading_messages">
            <PulseLoader color="var(--blue-color)" size={10} />
          </div>
        ) : messages.length === 0 ? (
          <div className="no_messages">
            <p>No messages yet</p>
            <span>Send a message to start the conversation!</span>
          </div>
        ) : (
          <div className="messages_list">
            {messages.map((message, index) => {
              const prevMessage = messages[index - 1];
              const showDate =
                !prevMessage ||
                new Date(message.createdAt).toDateString() !==
                  new Date(prevMessage.createdAt).toDateString();

              return (
                <div key={message._id}>
                  {showDate && (
                    <div className="date_divider">
                      <span>
                        {new Date(message.createdAt).toLocaleDateString(
                          "pt-PT",
                          {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </span>
                    </div>
                  )}
                  <MessageBubble
                    message={message}
                    isMine={message.sender?._id === user.id}
                    isGroup={conversation.type === "group"}
                    onReply={handleReply}
                    onReact={onReact}
                    onDelete={onDelete}
                    user={user}
                  />
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button className="scroll_to_bottom_btn" onClick={scrollToBottom}>
            {unreadCount > 0 && (
              <span className="unread_badge">{unreadCount}</span>
            )}
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path
                fill="currentColor"
                d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Message Input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        onTypingStart={onTypingStart}
        onTypingStop={onTypingStop}
        replyTo={replyTo}
        onCancelReply={handleCancelReply}
        user={user}
      />

      {/* Group Info Panel */}
      {showGroupInfo && conversation?.type === "group" && (
        <GroupInfo
          conversation={conversation}
          onClose={() => setShowGroupInfo(false)}
          user={user}
        />
      )}
    </div>
  );
}
